import { BreakpointObserver } from '@angular/cdk/layout';
import { CdkScrollable } from '@angular/cdk/scrolling';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  runInInjectionContext,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TellentAssistantComponent } from '@core/tellent-assistant-smart-ui';
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import {
  injectTellentNavigationItems,
  provideTellentNavigationController,
  provideTellentNavigationItems,
  TellentAppShellStateService,
  TellentMainNavigationController,
  TellentMainNavigationStateService,
  TellentNavigationDataFacade,
  TellentNavigationViewModelBuilder,
} from '@core/tellent-logic';
import { TellentDevFlag, TellentNavigationItem } from '@core/tellent-types';
import { toLazySignal } from '@core/tellent-util';
import { Breakpoint } from '@core/user-interface-legacy';
import { WalkthroughStepId } from '@core/walkthrough-types';

import { TellentMainNavigationToggleComponent } from './main-navigation-toggle/main-navigation-toggle.component';
import { TellentAppSwitcherNextComponent } from '../../tellent-app-switcher-next/tellent-app-switcher-next.component';
import { TellentNavigationComponent } from '../navigation.component';

const NAV_REGION_TOLERANCE_PX = 4;

@Component({
  selector: 'tlc-main-navigation',
  styleUrls: [
    './main-navigation.component.desktop.less',
    './main-navigation.component.mobile.less',
    './main-navigation.component.less',
  ],
  templateUrl: './main-navigation.component.html',
  imports: [
    TellentNavigationComponent,
    TellentAppSwitcherNextComponent,
    TellentMainNavigationToggleComponent,
    TellentAssistantComponent,
    CdkScrollable,
  ],
  host: {
    '(mouseenter)': '!shellState.isMobile() ? onMouseEnter() : null',
    '(mouseleave)': '!shellState.isMobile() ? onMouseLeave($event) : null',
    '[class.hovered]': 'navState.hovered()',
    '[class.locked]': 'navState.forceMaximized()',
    '[class.minimized]': 'navState.minimized()',
    '[class.has-assistant]': 'tellentAssistantFlag()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    provideTellentNavigationItems({
      useFactory: () => {
        const existingItems = injectTellentNavigationItems({ optional: true, skipSelf: true });
        if (existingItems) return existingItems;
        return <Signal<TellentNavigationItem[]>>(
          toLazySignal(inject(TellentNavigationDataFacade).navigationMain$, { initialValue: [] })
        );
      },
    }),
    provideTellentNavigationController({ useClass: TellentMainNavigationController }),
    TellentNavigationViewModelBuilder,
  ],
})
export class TellentMainNavigationComponent {
  #injector = inject(Injector);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly tellentAssistantFlag = injectDevFlagSignal(TellentDevFlag.TellentAssistant);

  public shellState = inject(TellentAppShellStateService);
  public navState = inject(TellentMainNavigationStateService);
  public appSwitcherWalkthroughId = input<WalkthroughStepId>();
  public navigationWalkthroughId = input<WalkthroughStepId>();
  public shouldExpandNavOnHover = input(true);

  public appSwitcherWidthChange = output<number>();

  public appSwitcher = viewChild(TellentAppSwitcherNextComponent);

  #isDropdownOpen = signal(false);

  public ngOnInit() {
    this.breakpointObserver
      .observe(Breakpoint.DESKTOP_MD_DOWN)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (!this.shellState.isMobile() && value.matches) this.toggleSidebarMinimized(true);
      });
  }

  public ngAfterViewInit() {
    this.emitOffsetChange();
  }

  public onMouseEnter() {
    if (!this.shouldExpandNavOnHover() || !this.navState.minimized()) return;

    this.navState.hovered.set(true);
    this.navState.minimized.set(false);
  }

  public onMouseLeave(event: MouseEvent) {
    this.maybeCollapse(event);
  }

  public onAssistantTriggerLeave(event: MouseEvent) {
    this.maybeCollapse(event);
  }

  public onDropdownOpenChange(open: boolean) {
    this.#isDropdownOpen.set(open);

    if (!open && !this.navState.forceMaximized()) {
      this.navState.minimized.set(true);
    }
  }

  public toggleSidebarMinimized(val: boolean) {
    this.navState.forceMaximized.set(!val);
    this.navState.minimized.set(val);
    this.emitOffsetChange();
  }

  private maybeCollapse(event: MouseEvent) {
    if (!this.shouldExpandNavOnHover() || this.navState.forceMaximized() || this.#isDropdownOpen())
      return;

    // Trigger is a shadow-DOM CDK overlay (relatedTarget unreliable) sitting within the sidebar's
    // rect, so decide by pointer position rather than the event target.
    if (this.pointerInNavRegion(event.clientX, event.clientY)) return;

    this.collapse();
  }

  private pointerInNavRegion(x: number, y: number): boolean {
    return this.inRect(x, y, this.elementRef.nativeElement.getBoundingClientRect());
  }

  private inRect(x: number, y: number, rect: DOMRect): boolean {
    const t = NAV_REGION_TOLERANCE_PX;
    return x >= rect.left - t && x <= rect.right + t && y >= rect.top - t && y <= rect.bottom + t;
  }

  private collapse() {
    this.navState.hovered.set(false);
    this.navState.minimized.set(true);
    this.emitOffsetChange();
  }

  private emitOffsetChange() {
    runInInjectionContext(this.#injector, () => {
      afterNextRender(() => {
        const bbox = this.appSwitcher()?.triggerEl()?.nativeElement.getBoundingClientRect();

        this.appSwitcherWidthChange.emit(bbox.right);
      });
    });
  }
}
